import { useEffect, useState, useMemo, useRef, memo } from "react";
import { useSelector } from "react-redux";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { MultiSelect } from "../ui/multi-select";
import { Filter, X, Link as LinkIcon, Trash2, Bookmark } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { selectLeadStatuses } from "../../store/slices/leadStatusSlice";
import { fetchDesks } from "../../services/crmServices/deskService";
import { searchUsers, fetchAvailableUsers } from "../../services/crmServices/userService";
import { fetchDistinctValues, fetchCountriesBySourceAffiliate, fetchStatusesBySourceAffiliate, fetchSourcesByAffiliate } from "../../services/crmServices/leadService";
import { getFullName } from "../../services/common/util";
import { selectCurrentFilters } from "../../store/slices/leadsSlice";
import { Country } from "country-state-city";
import { searchPositions } from "../../services/crmServices/positionService";
import { useAuth } from "../../hooks/useAuth";
import { getFilterShortcuts, createFilterShortcut, updateFilterShortcut, deleteFilterShortcut } from "../../services/crmServices/filterShortcutService";
export const LeadFilters = memo(function LeadFilters({ onFilter, onClear, allLeads = [] }) {
    const { user } = useAuth();
    const isAdmin = user?.position?.role === "admin";
    const isFloorManager = user?.position?.role === "floorManager";
    const isDeskManager = user?.position?.role === "deskManager";
    const canFilterByAgent = isAdmin || isFloorManager || isDeskManager;
    // Get current filters from Redux state
    const savedFilters = useSelector(selectCurrentFilters);
    const leadStatuses = useSelector(selectLeadStatuses);

    const [filters, setFilters] = useState({});
    const [isOpen, setIsOpen] = useState(false);
    const hasInitializedRef = useRef(false);
    const [showSaveShortcutModal, setShowSaveShortcutModal] = useState(false);
    const [shortcutName, setShortcutName] = useState("");
    const [savedShortcuts, setSavedShortcuts] = useState([]);

    const [allDesks, setAllDesks] = useState([]);
    const [allAgents, setAllAgents] = useState([]); // For agent filter (actual agents)
    const [allFloorManagers, setAllFloorManagers] = useState([]);
    const [sources, setSources] = useState([]);
    const [affiliates, setAffiliates] = useState([]);
    const [countryOptions, setCountryOptions] = useState([]);

    // Add refs to cache positions and distinct values to avoid multiple API calls
    const agentPositionsRef = useRef(null);
    const floorManagerPositionsRef = useRef(null);
    const distinctCountriesRef = useRef(null);
    const loadingSourcesByAffiliateRef = useRef(false);
    const sourcesSetByAffiliateRef = useRef(false);

    // Initialize filters with saved state only when sheet first opens
    useEffect(() => {
        if (!isOpen) {
            // Reset initialization flag when sheet closes
            hasInitializedRef.current = false;
            sourcesSetByAffiliateRef.current = false; // Reset flag when sheet closes
            return;
        }
        
        // Only initialize once when sheet opens, not on every render
        if (hasInitializedRef.current) return;
        hasInitializedRef.current = true;
        
        const processedFilters = savedFilters ? { ...savedFilters } : {};
        
        // Remove excludeCountries - we're using country (include) filter now
        if (processedFilters.excludeCountries) {
            delete processedFilters.excludeCountries;
        }
        
        // Ensure country filter is an array (can be empty - no countries selected by default)
        if (processedFilters.country && !Array.isArray(processedFilters.country)) {
            processedFilters.country = [processedFilters.country];
        }
        
        // Only update filters when sheet first opens
        setFilters(processedFilters);
    }, [isOpen, savedFilters]);

    // OPTIMIZED: Load filter data in parallel where possible
    useEffect(() => {
        const loadFilterData = async () => {
            try {
                // STEP 1: Fetch all positions once (both agent and floorManager)
                const [agentPositionsResponse, floorManagerPositionsResponse] = await Promise.all([
                    searchPositions({ role: "agent" }),
                    isAdmin ? searchPositions({ role: "floorManager" }) : Promise.resolve({ data: [] })
                ]);
                
                // Cache positions in refs
                agentPositionsRef.current = agentPositionsResponse?.data || [];
                floorManagerPositionsRef.current = floorManagerPositionsResponse?.data || [];
                
                // STEP 2: Parallel fetch for independent data (desks, floor managers)
                const promises = [
                    fetchDesks()
                ];
                
                if (isAdmin) {
                    promises.push(getFloorManagers(floorManagerPositionsRef.current)); // Pass cached positions
                }
                
                const results = await Promise.all(promises);
                const desksResponse = results[0];

                // Process desks
                if (desksResponse?.data?.length) {
                    let filteredDesks = desksResponse.data;
                    
                    // Filter desks based on user role
                    if (isDeskManager) {
                        // Desk manager sees only their managed desks
                        filteredDesks = desksResponse.data.filter(desk => {
                            const managerId = desk.manager?._id || desk.manager;
                            const userId = user._id?.toString();
                            const managerIdStr = managerId?.toString();
                            return managerIdStr === userId;
                        });
                    } else if (isFloorManager) {
                        // Floor manager sees only desks they manage (same floor)
                        filteredDesks = desksResponse.data.filter(desk => {
                            const floorManagerId = desk.floorManager?._id || desk.floorManager;
                            const userId = user._id?.toString();
                            const floorManagerIdStr = floorManagerId?.toString();
                            return floorManagerIdStr === userId && desk.floor === user.position.floor;
                        });
                    }
                    // Admin sees all desks (no filtering)
                    
                    setAllDesks(filteredDesks);
                    // After desks are loaded, fetch agents based on user role
                    if (canFilterByAgent) {
                        getAgents(filteredDesks, agentPositionsRef.current); // Pass cached positions
                    }
                }
            } catch (error) {
                console.error("Error loading filter data:", error);
            }
        };
        if (isOpen) {
            loadFilterData();
        }
    }, [isAdmin, isDeskManager, isFloorManager, canFilterByAgent, user._id, user.position.floor, isOpen]);

    // Updated to use cached agent positions only (no fallback fetch)
    const getAgents = async (desks = [], agentPositions = null) => {
        try {
            let agentsResponse;
            
            // Use cached positions - should always be available from useEffect
            const agentPositionsData = agentPositions || agentPositionsRef.current || [];
            
            if (isAdmin) {
                // Admin sees all users
                agentsResponse = await fetchAvailableUsers();
            } else if (isFloorManager) {
                // Floor manager sees agents from their managed desks
                const managedDesks = desks.filter(desk => {
                    const floorManagerId = desk.floorManager?._id || desk.floorManager;
                    return (floorManagerId === user._id || floorManagerId?.toString() === user._id?.toString()) &&
                           desk.floor === user.position.floor;
                });
                const managedDeskIds = managedDesks.map(desk => desk._id?._id || desk._id).filter(Boolean);
                
                if (managedDeskIds.length > 0 && agentPositionsData.length > 0) {
                    agentsResponse = await searchUsers({ 
                        position: { $in: agentPositionsData.map((pos) => pos._id) },
                        agentDesk: { $in: managedDeskIds }
                    });
                }
            } else if (isDeskManager) {
                // Desk manager sees agents from their managed desks only
                const managedDesks = desks.filter(desk => {
                    // Handle both populated object and ID string
                    const managerId = desk.manager?._id || desk.manager;
                    const userId = user._id?.toString();
                    const managerIdStr = managerId?.toString();
                    return managerIdStr === userId;
                });
                
                // Normalize desk IDs to strings for comparison
                const managedDeskIds = managedDesks.map(desk => {
                    const deskId = desk._id?._id || desk._id;
                    return deskId?.toString();
                }).filter(Boolean);
                
                if (managedDeskIds.length > 0 && agentPositionsData.length > 0) {
                    agentsResponse = await searchUsers({ 
                        position: { $in: agentPositionsData.map((pos) => pos._id) },
                        agentDesk: { $in: managedDeskIds }
                    });
                }
            }
            
            if (agentsResponse?.data?.length) {
                setAllAgents(agentsResponse.data);
            } else {
                setAllAgents([]);
            }
        } catch (error) {
            console.error("Error fetching agents:", error);
            setAllAgents([]);
        }
    };

    // Updated to use cached floor manager positions only (no fallback fetch)
    const getFloorManagers = async (floorManagerPositions = null) => {
        // Use cached positions - should always be available from useEffect
        const floorManagerPositionsData = floorManagerPositions || floorManagerPositionsRef.current || [];
        
        if (floorManagerPositionsData.length > 0) {
            const floorManagersResponse = await searchUsers({
                position: { $in: floorManagerPositionsData.map((pos) => pos._id) }
            });
            if (floorManagersResponse?.data?.length) {
                setAllFloorManagers(floorManagersResponse.data);
            }
        }
    };

    // OPTIMIZED: Fetch distinct sources, affiliates, and countries in parallel
    useEffect(() => {
        const loadDistinctValues = async () => {
            try {
                // Get current affiliate value - don't load sources if affiliate is selected
                // Sources will be loaded by the affiliate-specific useEffect
                const currentAffiliate = filters.affiliate;
                const hasAffiliate = currentAffiliate && Array.isArray(currentAffiliate) && currentAffiliate.length > 0;
                
                // Parallel fetch for distinct values (sources only if no affiliate, affiliates, countries)
                const promises = [
                    hasAffiliate ? Promise.resolve(null) : fetchDistinctValues('source'), // Skip sources if affiliate is selected
                    fetchDistinctValues('affiliate'),
                    fetchDistinctValues('country')
                ];
                
                const [sourcesResponse, affiliatesResponse, countriesResponse] = await Promise.all(promises);
                
                // Only set sources if we fetched them AND no affiliate is selected AND affiliate useEffect hasn't set sources
                // This prevents race condition where affiliate useEffect sets filtered sources but this one overwrites them
                if (!hasAffiliate && sourcesResponse?.data && !sourcesSetByAffiliateRef.current) {
                    setSources(sourcesResponse.data.map((source) => ({ label: source, value: source })));
                    sourcesSetByAffiliateRef.current = false; // Reset flag
                }
                
                if (affiliatesResponse?.data) {
                    setAffiliates(affiliatesResponse.data.map((affiliate) => ({ label: affiliate, value: affiliate })));
                }
                
                // Cache countries for later use
                if (countriesResponse?.data && countriesResponse.data.length > 0) {
                    const dbCountries = countriesResponse.data.map((country) => ({
                        label: country,
                        value: country
                    }));
                    
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    
                    const countryMap = new Map();
                    dbCountries.forEach(country => {
                        countryMap.set(country.value, country);
                    });
                    libraryCountries.forEach(country => {
                        if (!countryMap.has(country.value)) {
                            countryMap.set(country.value, country);
                        }
                    });
                    
                    const combinedCountries = Array.from(countryMap.values()).sort((a, b) => 
                        a.label.localeCompare(b.label)
                    );
                    
                    distinctCountriesRef.current = combinedCountries;
                    setCountryOptions(combinedCountries);
                } else {
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    distinctCountriesRef.current = libraryCountries;
                    setCountryOptions(libraryCountries);
                }
            } catch (error) {
                console.error("Error fetching distinct values:", error);
                // Fallback to extracting from allLeads if API fails
                // BUT: Don't set sources if affiliate is selected (let the affiliate useEffect handle it)
                const currentAffiliate = filters.affiliate;
                const hasAffiliate = currentAffiliate && Array.isArray(currentAffiliate) && currentAffiliate.length > 0;
                
                if (allLeads.length > 0) {
                    // Only set sources if no affiliate is selected AND affiliate useEffect hasn't set sources
                    if (!hasAffiliate && !sourcesSetByAffiliateRef.current) {
                        const uniqueSources = [...new Set(allLeads.map((lead) => lead.source).filter(Boolean))];
                        setSources(uniqueSources.map((source) => ({ label: source, value: source })));
                        sourcesSetByAffiliateRef.current = false; // Reset flag
                    }
                    
                    const uniqueAffiliates = [...new Set(allLeads.map((lead) => lead.affiliate).filter(Boolean))];
                    setAffiliates(uniqueAffiliates.map((affiliate) => ({ label: affiliate, value: affiliate })));
                    
                    const uniqueCountries = [...new Set(allLeads.map((lead) => lead.country).filter(Boolean))];
                    
                    // For countries, combine with library countries
                    const dbCountries = uniqueCountries.map((country) => ({
                        label: country,
                        value: country
                    }));
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    
                    const countryMap = new Map();
                    dbCountries.forEach(country => countryMap.set(country.value, country));
                    libraryCountries.forEach(country => {
                        if (!countryMap.has(country.value)) {
                            countryMap.set(country.value, country);
                        }
                    });
                    
                    distinctCountriesRef.current = Array.from(countryMap.values()).sort((a, b) => 
                        a.label.localeCompare(b.label)
                    );
                    setCountryOptions(distinctCountriesRef.current);
                } else {
                    // Final fallback: just use library countries
                    const libraryCountries = Country.getAllCountries().map((country) => ({
                        label: country.name,
                        value: country.name
                    }));
                    distinctCountriesRef.current = libraryCountries;
                    setCountryOptions(libraryCountries);
                }
            }
        };
        
        if (isOpen) {
            loadDistinctValues();
        }
    }, [isOpen]); // Only run when sheet opens - affiliate useEffect handles source filtering

    // Create a stable key for source/affiliate changes
    const sourceAffiliateKey = useMemo(() => {
        const sourceKey = filters.source && Array.isArray(filters.source) ? [...filters.source].sort().join(',') : '';
        const affiliateKey = filters.affiliate && Array.isArray(filters.affiliate) ? [...filters.affiliate].sort().join(',') : '';
        return `${sourceKey}|${affiliateKey}`;
    }, [filters.source, filters.affiliate]);

    // Create a stable key for affiliate changes
    const affiliateKey = useMemo(() => {
        if (filters.affiliate && Array.isArray(filters.affiliate) && filters.affiliate.length > 0) {
            return [...filters.affiliate].sort().join(',');
        }
        return '';
    }, [filters.affiliate]);

    // Fetch sources filtered by affiliate when affiliate is selected
    useEffect(() => {
        const loadSourcesByAffiliate = async () => {
            // Only fetch if sheet is open
            if (!isOpen) {
                return;
            }

            // Prevent concurrent loads
            if (loadingSourcesByAffiliateRef.current) {
                return;
            }

            // Get current affiliate value from state
            const currentAffiliate = filters.affiliate;
            const hasAffiliate = currentAffiliate && Array.isArray(currentAffiliate) && currentAffiliate.length > 0;

            // If affiliate is selected, fetch sources for that affiliate
            if (hasAffiliate) {
                loadingSourcesByAffiliateRef.current = true;
                sourcesSetByAffiliateRef.current = true; // Mark that we're setting sources by affiliate
                try {
                    const sourcesResponse = await fetchSourcesByAffiliate(currentAffiliate);

                    if (sourcesResponse?.data && sourcesResponse.data.length > 0) {
                        // Format sources
                        const sourcesList = sourcesResponse.data.map((source) => ({
                            label: source,
                            value: source
                        }));
                        setSources(sourcesList);
                        
                        // Clear any selected sources that don't belong to the selected affiliate(s)
                        setFilters(prev => {
                            const currentSelectedSources = prev.source || [];
                            if (currentSelectedSources.length > 0) {
                                const validSourceValues = sourcesList.map(s => s.value);
                                const validSelectedSources = currentSelectedSources.filter(source => 
                                    validSourceValues.includes(source)
                                );
                                
                                // Only update if we need to remove invalid selections
                                if (validSelectedSources.length !== currentSelectedSources.length) {
                                    return {
                                        ...prev,
                                        source: validSelectedSources
                                    };
                                }
                            }
                            return prev;
                        });
                    } else {
                        // If no sources found for this affiliate, set empty array and clear selected sources
                        setSources([]);
                        setFilters(prev => {
                            if (prev.source && prev.source.length > 0) {
                                return {
                                    ...prev,
                                    source: []
                                };
                            }
                            return prev;
                        });
                    }
                } catch (error) {
                    console.error("Error fetching sources by affiliate:", error);
                    // On error, don't fallback to all sources - keep empty or show error
                    setSources([]);
                } finally {
                    loadingSourcesByAffiliateRef.current = false;
                }
            } else {
                // No affiliate selected - fetch all sources
                sourcesSetByAffiliateRef.current = false; // Reset flag when no affiliate
                try {
                    const allSourcesResponse = await fetchDistinctValues('source');
                    if (allSourcesResponse?.data) {
                        setSources(allSourcesResponse.data.map((source) => ({ label: source, value: source })));
                    }
                } catch (error) {
                    console.error("Error fetching all sources:", error);
                }
            }
        };

        loadSourcesByAffiliate();
    }, [isOpen, filters.affiliate]);

    // Fetch countries with counts when source or affiliate is selected
    useEffect(() => {
        const loadCountriesWithCounts = async () => {
            // Only fetch if sheet is open
            if (!isOpen) {
                return;
            }

            const hasSource = filters.source && Array.isArray(filters.source) && filters.source.length > 0;
            const hasAffiliate = filters.affiliate && Array.isArray(filters.affiliate) && filters.affiliate.length > 0;

            // If source or affiliate is selected, fetch countries with counts
            if (hasSource || hasAffiliate) {
                try {
                    const countriesResponse = await fetchCountriesBySourceAffiliate(
                        hasSource ? filters.source : [],
                        hasAffiliate ? filters.affiliate : []
                    );

                    if (countriesResponse?.data && countriesResponse.data.length > 0) {
                        // Format countries with counts
                        const countriesWithCounts = countriesResponse.data.map((item) => ({
                            label: item.country,
                            value: item.country,
                            count: item.count
                        }));

                        // Sort alphabetically by country name
                        const sortedCountries = countriesWithCounts.sort((a, b) =>
                            a.value.localeCompare(b.value)
                        );

                        setCountryOptions(sortedCountries);
                    } else {
                        // No countries found for this source/affiliate
                        setCountryOptions([]);
                    }
                } catch (error) {
                    console.error("Error fetching countries by source/affiliate:", error);
                    // On error, fall back to cached countries
                    if (distinctCountriesRef.current) {
                        setCountryOptions(distinctCountriesRef.current);
                    }
                }
            } else {
                // No source or affiliate selected - use cached countries (no API call)
                if (distinctCountriesRef.current) {
                    setCountryOptions(distinctCountriesRef.current);
                } else {
                    // Fallback: reload if cache is empty (shouldn't happen)
                    const loadNormalCountries = async () => {
                        try {
                            const countriesResponse = await fetchDistinctValues('country');
                            if (countriesResponse?.data && countriesResponse.data.length > 0) {
                                const dbCountries = countriesResponse.data.map((country) => ({
                                    label: country,
                                    value: country
                                }));

                                const libraryCountries = Country.getAllCountries().map((country) => ({
                                    label: country.name,
                                    value: country.name
                                }));

                                const countryMap = new Map();
                                dbCountries.forEach(country => {
                                    countryMap.set(country.value, country);
                                });
                                libraryCountries.forEach(country => {
                                    if (!countryMap.has(country.value)) {
                                        countryMap.set(country.value, country);
                                    }
                                });

                                const combinedCountries = Array.from(countryMap.values()).sort((a, b) =>
                                    a.label.localeCompare(b.label)
                                );

                                distinctCountriesRef.current = combinedCountries;
                                setCountryOptions(combinedCountries);
                            } else {
                                const libraryCountries = Country.getAllCountries().map((country) => ({
                                    label: country.name,
                                    value: country.name
                                }));
                                distinctCountriesRef.current = libraryCountries;
                                setCountryOptions(libraryCountries);
                            }
                        } catch (error) {
                            console.error("Error loading normal countries:", error);
                        }
                    };
                    loadNormalCountries();
                }
            }
        };

        // Debounce to avoid too many API calls
        const timeoutId = setTimeout(() => {
            loadCountriesWithCounts();
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [sourceAffiliateKey, isOpen, filters.source, filters.affiliate]); // Watch for source/affiliate changes

    // State for status options with counts
    const [statusOptionsWithCounts, setStatusOptionsWithCounts] = useState([]);

    // Calculate or fetch status counts based on source/affiliate filters (similar to countries)
    useEffect(() => {
        const loadStatusCounts = async () => {
            // Only fetch if sheet is open
            if (!isOpen) {
                return;
            }

            const hasSource = filters.source && Array.isArray(filters.source) && filters.source.length > 0;
            const hasAffiliate = filters.affiliate && Array.isArray(filters.affiliate) && filters.affiliate.length > 0;

            if (!leadStatuses || leadStatuses.length === 0) {
                setStatusOptionsWithCounts(leadStatuses || []);
                return;
            }

            // If source or affiliate is selected, fetch status counts from API (not just current page)
            if (hasSource || hasAffiliate) {
                try {
                    const statusesResponse = await fetchStatusesBySourceAffiliate(
                        hasSource ? filters.source : [],
                        hasAffiliate ? filters.affiliate : []
                    );

                    if (statusesResponse?.data && statusesResponse.data.length > 0) {
                        // Create a map of status counts from API response
                        const statusCountsMap = new Map();
                        statusesResponse.data.forEach(item => {
                            statusCountsMap.set(item.status, item.count);
                        });

                        // Map leadStatuses with counts from API
                        const statusesWithCounts = leadStatuses.map(status => {
                            const statusValue = status.value || status;
                            return {
                                ...status,
                                count: statusCountsMap.get(statusValue) || 0
                            };
                        });

                        setStatusOptionsWithCounts(statusesWithCounts);
                    } else {
                        // If API returns empty, set all counts to 0
                        setStatusOptionsWithCounts(leadStatuses.map(status => ({
                            ...status,
                            count: 0
                        })));
                    }
                } catch (error) {
                    console.error("Error fetching status counts:", error);
                    // Fallback to calculating from allLeads on error
                    let filteredLeads = allLeads;
                    if (hasSource) {
                        filteredLeads = filteredLeads.filter(lead => 
                            lead.source && filters.source.includes(lead.source)
                        );
                    }
                    if (hasAffiliate) {
                        filteredLeads = filteredLeads.filter(lead => 
                            lead.affiliate && filters.affiliate.includes(lead.affiliate)
                        );
                    }
                    const statusCounts = new Map();
                    filteredLeads.forEach(lead => {
                        if (lead.status) {
                            const statusValue = typeof lead.status === 'string' ? lead.status : lead.status?.value || lead.status;
                            statusCounts.set(statusValue, (statusCounts.get(statusValue) || 0) + 1);
                        }
                    });
                    const statusesWithCounts = leadStatuses.map(status => {
                        const statusValue = status.value || status;
                        return {
                            ...status,
                            count: statusCounts.get(statusValue) || 0
                        };
                    });
                    setStatusOptionsWithCounts(statusesWithCounts);
                }
            } else {
                // No source/affiliate filter - calculate from allLeads (current page only)
                let filteredLeads = allLeads;
                const statusCounts = new Map();
                filteredLeads.forEach(lead => {
                    if (lead.status) {
                        const statusValue = typeof lead.status === 'string' ? lead.status : lead.status?.value || lead.status;
                        statusCounts.set(statusValue, (statusCounts.get(statusValue) || 0) + 1);
                    }
                });
                const statusesWithCounts = leadStatuses.map(status => {
                    const statusValue = status.value || status;
                    return {
                        ...status,
                        count: statusCounts.get(statusValue) || 0
                    };
                });
                setStatusOptionsWithCounts(statusesWithCounts);
            }
        };

        loadStatusCounts();
    }, [isOpen, leadStatuses, allLeads, filters.source, filters.affiliate]);

    const handleApplyFilters = () => {
        const processedFilters = { ...filters };
        
        // Remove excludeCountries - we're using country (include) filter now
        delete processedFilters.excludeCountries;
        
        // If no countries are selected, remove country filter (show all)
        if (!processedFilters.country || (Array.isArray(processedFilters.country) && processedFilters.country.length === 0)) {
            delete processedFilters.country;
        }
        
        // Ensure country is always an array if it exists
        if (processedFilters.country && !Array.isArray(processedFilters.country)) {
            processedFilters.country = [processedFilters.country];
        }
        
        // Remove any undefined or empty array filters
        Object.keys(processedFilters).forEach(key => {
            if (processedFilters[key] === undefined || 
                processedFilters[key] === null ||
                (Array.isArray(processedFilters[key]) && processedFilters[key].length === 0)) {
                delete processedFilters[key];
            }
        });
        
        onFilter(processedFilters);
        setIsOpen(false);
    };

    const handleClearFilters = () => {
        setFilters({});
        onClear();
        setIsOpen(false);
    };

    // Load saved shortcuts from API
    useEffect(() => {
        const loadShortcuts = async () => {
            try {
                const response = await getFilterShortcuts();
                if (response?.data) {
                    // Map API response to match expected format
                    const mappedShortcuts = response.data.map(shortcut => ({
                        id: shortcut._id,
                        name: shortcut.name,
                        filters: shortcut.filters,
                        createdAt: shortcut.createdAt,
                        updatedAt: shortcut.updatedAt
                    }));
                    setSavedShortcuts(mappedShortcuts);
                }
            } catch (error) {
                console.error("Error loading shortcuts:", error);
                // Fallback to localStorage for migration
                try {
                    const shortcuts = localStorage.getItem('leadFilterShortcuts');
                    if (shortcuts) {
                        const parsed = JSON.parse(shortcuts);
                        setSavedShortcuts(parsed);
                        // Optionally migrate to backend
                        if (parsed.length > 0) {
                            migrateShortcutsToBackend(parsed);
                        }
                    }
                } catch (localError) {
                    console.error("Error loading from localStorage:", localError);
                }
            }
        };
        loadShortcuts();
    }, []);

    // Helper function to migrate localStorage shortcuts to backend
    const migrateShortcutsToBackend = async (localShortcuts) => {
        try {
            for (const shortcut of localShortcuts) {
                await createFilterShortcut({
                    name: shortcut.name,
                    filters: shortcut.filters
                });
            }
            // Clear localStorage after migration
            localStorage.removeItem('leadFilterShortcuts');
        } catch (error) {
            console.error("Error migrating shortcuts:", error);
        }
    };

    const handleSaveShortcut = () => {
        // Check if there are any filters to save
        const hasFilters = Object.keys(filters).length > 0 && 
            Object.values(filters).some(value => {
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === 'string') return value.trim() !== '';
                return value !== null && value !== undefined;
            });

        if (!hasFilters) {
            toast.error("Please set some filters before saving a shortcut");
            return;
        }

        setShowSaveShortcutModal(true);
    };

    const handleConfirmSaveShortcut = async () => {
        if (!shortcutName.trim()) {
            toast.error("Please enter a name for the shortcut");
            return;
        }

        try {
            // Check if shortcut with same name exists
            const existingShortcut = savedShortcuts.find(
                s => s.name.toLowerCase() === shortcutName.trim().toLowerCase()
            );

            let response;
            if (existingShortcut) {
                // Update existing shortcut
                response = await updateFilterShortcut(existingShortcut.id, {
                    name: shortcutName.trim(),
                    filters: { ...filters }
                });
                toast.success("Shortcut updated successfully");
            } else {
                // Create new shortcut
                response = await createFilterShortcut({
                    name: shortcutName.trim(),
                    filters: { ...filters }
                });
                toast.success("Shortcut saved successfully");
            }

            // Refresh shortcuts list
            const shortcutsResponse = await getFilterShortcuts();
            if (shortcutsResponse?.data) {
                const mappedShortcuts = shortcutsResponse.data.map(shortcut => ({
                    id: shortcut._id,
                    name: shortcut.name,
                    filters: shortcut.filters,
                    createdAt: shortcut.createdAt,
                    updatedAt: shortcut.updatedAt
                }));
                setSavedShortcuts(mappedShortcuts);
            }

            setShortcutName("");
            setShowSaveShortcutModal(false);
        } catch (error) {
            console.error("Error saving shortcut:", error);
            toast.error(error.response?.data?.message || "Failed to save shortcut");
        }
    };

    const handleLoadShortcut = (shortcut) => {
        setFilters(shortcut.filters);
        toast.success(`Loaded shortcut: ${shortcut.name}`);
    };

    const handleDeleteShortcut = async (shortcutId) => {
        try {
            await deleteFilterShortcut(shortcutId);
            
            // Update local state
            const updatedShortcuts = savedShortcuts.filter(s => s.id !== shortcutId);
            setSavedShortcuts(updatedShortcuts);
            toast.success("Shortcut deleted");
        } catch (error) {
            console.error("Error deleting shortcut:", error);
            toast.error(error.response?.data?.message || "Failed to delete shortcut");
        }
    };

    // Helper function to update filter values
    const updateFilter = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    // Count active filters for display
    const activeFiltersCount = Object.keys(filters).filter((key) => {
        const value = filters[key];
        if (Array.isArray(value)) {
            return value.length > 0;
        }
        return value && value.toString().trim() !== "";
    }).length;

    return (
        <>
        {/* Filter Trigger Button */}
        <button
            onClick={() => setIsOpen(true)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors relative"
        >
            <Filter className="h-4 w-4 mr-2" />
            Filters
            {activeFiltersCount > 0 && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold text-white bg-blue-600 rounded-full">
                    {activeFiltersCount}
                </span>
            )}
        </button>

        {/* Filter Modal */}
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50/80">
                    <div>
                        <DialogTitle className="text-lg font-semibold text-gray-900">Filters</DialogTitle>
                        <DialogDescription className="text-xs text-gray-500 mt-0.5">Refine your lead search results</DialogDescription>
                    </div>
                    <div className="flex items-center gap-2">
                        {activeFiltersCount > 0 && (
                            <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-full">
                                {activeFiltersCount} active
                            </span>
                        )}
                    </div>
                </div>

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                    <div className="grid grid-cols-2 gap-x-8 gap-y-5">

                        {/* LEFT COLUMN */}
                        <div className="space-y-5">
                            {/* Date Filters */}
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Date Range</Label>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <Label htmlFor="dateFrom" className="text-[10px] text-gray-500 mb-1">From</Label>
                                        <Input
                                            id="dateFrom"
                                            type="date"
                                            value={filters.registeredFrom || ""}
                                            onChange={(e) => updateFilter("registeredFrom", e.target.value)}
                                            className="text-xs h-8"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="dateTo" className="text-[10px] text-gray-500 mb-1">To</Label>
                                        <Input
                                            id="dateTo"
                                            type="date"
                                            value={filters.registeredTo || ""}
                                            onChange={(e) => updateFilter("registeredTo", e.target.value)}
                                            className="text-xs h-8"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Source & Affiliate Filters */}
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Source & Affiliate</Label>
                                <div className="space-y-3">
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-[10px] text-gray-500">Source</Label>
                                            {(filters.source || []).length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => updateFilter("source", [])}
                                                    className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                                >
                                                    <X className="h-2.5 w-2.5" />
                                                </button>
                                            )}
                                        </div>
                                        <MultiSelect
                                            options={sources}
                                            selected={filters.source || []}
                                            onChange={(selected) => updateFilter("source", selected)}
                                            placeholder="Select sources"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-[10px] text-gray-500">Affiliate</Label>
                                            {(filters.affiliate || []).length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => updateFilter("affiliate", [])}
                                                    className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                                >
                                                    <X className="h-2.5 w-2.5" />
                                                </button>
                                            )}
                                        </div>
                                        <MultiSelect
                                            options={affiliates}
                                            selected={filters.affiliate || []}
                                            onChange={(selected) => updateFilter("affiliate", selected)}
                                            placeholder="Select affiliates"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Geographic Filters */}
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Geographic</Label>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label className="text-[10px] text-gray-500">Country</Label>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const allCountryValues = countryOptions.map(c => c.value);
                                                const currentSelected = filters.country || [];
                                                if (currentSelected.length === countryOptions.length) {
                                                    updateFilter("country", []);
                                                } else {
                                                    updateFilter("country", allCountryValues);
                                                }
                                            }}
                                            className="h-5 px-1.5 text-[10px] text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                                        >
                                            {(filters.country || []).length === countryOptions.length ? "Clear" : "All"}
                                        </button>
                                    </div>
                                    <MultiSelect
                                        options={countryOptions}
                                        selected={filters.country || []}
                                        onChange={(selected) => updateFilter("country", selected)}
                                        placeholder="Select countries to include"
                                    />
                                </div>
                            </div>

                            {/* Lead Indicators */}
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Indicators</Label>
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <Label className="text-[10px] text-gray-500">Live Leads</Label>
                                        {filters.isLive && (
                                            <button
                                                type="button"
                                                onClick={() => updateFilter("isLive", null)}
                                                className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                            >
                                                <X className="h-2.5 w-2.5" />
                                            </button>
                                        )}
                                    </div>
                                    <MultiSelect
                                        options={[
                                            { label: "Live (from affiliate)", value: "true" },
                                            { label: "Not Live", value: "false" }
                                        ]}
                                        selected={filters.isLive ? [filters.isLive] : []}
                                        onChange={(selected) => updateFilter("isLive", selected.length > 0 ? selected[0] : null)}
                                        placeholder="Filter by Live status"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* RIGHT COLUMN */}
                        <div className="space-y-5">
                            {/* Status Filters */}
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Statuses</Label>
                                <div className="space-y-3">
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-[10px] text-gray-500">Include Statuses</Label>
                                            <div className="flex items-center gap-1">
                                                {statusOptionsWithCounts.length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            updateFilter(
                                                                "statuses",
                                                                statusOptionsWithCounts.map(
                                                                    (status) => status.value || status.label || status
                                                                )
                                                            )
                                                        }
                                                        className="h-5 px-1.5 text-[10px] text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                                                    >
                                                        All
                                                    </button>
                                                )}
                                                {(filters.statuses || []).length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => updateFilter("statuses", [])}
                                                        className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                                    >
                                                        <X className="h-2.5 w-2.5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <MultiSelect
                                            options={statusOptionsWithCounts}
                                            selected={filters.statuses || []}
                                            onChange={(selected) => updateFilter("statuses", selected)}
                                            placeholder="Select statuses"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-[10px] text-gray-500">Exclude Statuses</Label>
                                            {(filters.excludeStatuses || []).length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => updateFilter("excludeStatuses", [])}
                                                    className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                                >
                                                    <X className="h-2.5 w-2.5" />
                                                </button>
                                            )}
                                        </div>
                                        <MultiSelect
                                            options={leadStatuses}
                                            selected={filters.excludeStatuses || []}
                                            onChange={(selected) => updateFilter("excludeStatuses", selected)}
                                            placeholder="Select statuses to exclude"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Desk & Agent Filters */}
                            <div className="space-y-2">
                                <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Desk & Agent</Label>
                                <div className="space-y-3">
                                    {isAdmin && (
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <Label className="text-[10px] text-gray-500">Floor Manager</Label>
                                                {(filters.floorManager || []).length > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => updateFilter("floorManager", [])}
                                                        className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                                    >
                                                        <X className="h-2.5 w-2.5" />
                                                    </button>
                                                )}
                                            </div>
                                            <MultiSelect
                                                options={[
                                                    { label: "Unassigned", value: "unassigned" },
                                                    ...allFloorManagers.map((floorManager) => ({
                                                        label: getFullName(floorManager),
                                                        value: floorManager._id
                                                    }))
                                                ]}
                                                selected={filters.floorManager || []}
                                                onChange={(selected) => updateFilter("floorManager", selected)}
                                                placeholder="Select floor managers"
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <Label className="text-[10px] text-gray-500">Desk</Label>
                                            {(filters.desk || []).length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => updateFilter("desk", [])}
                                                    className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                                >
                                                    <X className="h-2.5 w-2.5" />
                                                </button>
                                            )}
                                        </div>
                                        <MultiSelect
                                            options={[
                                                { label: "Unassigned", value: "unassigned" },
                                                ...allDesks.map((desk) => ({ label: desk.name, value: desk._id }))
                                            ]}
                                            selected={filters.desk || []}
                                            onChange={(selected) => updateFilter("desk", selected)}
                                            placeholder="Select desks"
                                        />
                                    </div>
                                    {canFilterByAgent && (
                                        <div>
                                            <div className="flex items-center justify-between mb-1">
                                                <Label className="text-[10px] text-gray-500">Agent</Label>
                                                <div className="flex items-center gap-1">
                                                    {allAgents.length > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                updateFilter(
                                                                    "agent",
                                                                    [
                                                                        "unassigned",
                                                                        ...allAgents.map((agent) => agent._id)
                                                                    ]
                                                                )
                                                            }
                                                            className="h-5 px-1.5 text-[10px] text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                                                        >
                                                            All
                                                        </button>
                                                    )}
                                                    {(filters.agent || []).length > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => updateFilter("agent", [])}
                                                            className="h-5 px-1.5 text-[10px] text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors inline-flex items-center gap-0.5"
                                                        >
                                                            <X className="h-2.5 w-2.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                            <MultiSelect
                                                options={[
                                                    { label: "Unassigned", value: "unassigned" },
                                                    ...allAgents.map((agent) => ({
                                                        label: getFullName(agent),
                                                        value: agent._id
                                                    }))
                                                ]}
                                                selected={filters.agent || []}
                                                onChange={(selected) => updateFilter("agent", selected)}
                                                placeholder="Select agents"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Saved Shortcuts */}
                            {savedShortcuts.length > 0 && (
                                <div className="space-y-2">
                                    <Label className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Shortcuts</Label>
                                    <div className="space-y-1 max-h-32 overflow-y-auto">
                                        {savedShortcuts.map((shortcut) => (
                                            <div
                                                key={shortcut.id}
                                                className="flex items-center justify-between p-1.5 bg-gray-50 rounded hover:bg-gray-100 transition-colors"
                                            >
                                                <button
                                                    onClick={() => handleLoadShortcut(shortcut)}
                                                    className="flex-1 text-left text-xs text-gray-700 hover:text-blue-600 transition-colors"
                                                >
                                                    <Bookmark className="h-2.5 w-2.5 inline mr-1.5 text-blue-500" />
                                                    {shortcut.name}
                                                </button>
                                                <button
                                                    onClick={() => handleDeleteShortcut(shortcut.id)}
                                                    className="h-6 w-6 p-0 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors flex items-center justify-center"
                                                    title="Delete shortcut"
                                                >
                                                    <Trash2 className="h-3 w-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50/80">
                    <button
                        onClick={handleSaveShortcut}
                        className="inline-flex items-center text-xs text-gray-500 hover:text-gray-900 transition-colors"
                    >
                        <LinkIcon className="h-3 w-3 mr-1.5" />
                        Save Shortcut
                    </button>
                    <div className="flex gap-2">
                        <button
                            onClick={handleClearFilters}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg transition-colors"
                        >
                            Clear All
                        </button>
                        <button
                            onClick={handleApplyFilters}
                            className="px-6 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>

        {/* Save Shortcut Modal */}
        <Dialog open={showSaveShortcutModal} onOpenChange={setShowSaveShortcutModal}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle>Save Filter Shortcut</DialogTitle>
                    <DialogDescription>
                        Give this filter configuration a name to save it for quick access later
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div>
                        <Label htmlFor="shortcutName">Shortcut Name</Label>
                        <Input
                            id="shortcutName"
                            value={shortcutName}
                            onChange={(e) => setShortcutName(e.target.value)}
                            placeholder="e.g., Active Leads from US"
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleConfirmSaveShortcut();
                                }
                            }}
                            autoFocus
                        />
                    </div>
                    <div className="flex space-x-3 justify-end">
                        <Button
                            variant="outline"
                            onClick={() => {
                                setShowSaveShortcutModal(false);
                                setShortcutName("");
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmSaveShortcut}
                            disabled={!shortcutName.trim()}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
        </>
    );
});
